import React, { useState } from "react";
import { Helmet } from "react-helmet";
import {
  FaWhatsapp,
  FaFacebookF,
  FaLinkedinIn,
  FaInstagram,
  FaStar,
} from "react-icons/fa";

import "./Contact.css";

import db from "../firebase";

import {
  addDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";


const SITE_URL = "https://www.samsoftix.com";


function Contact() {

  /* =========================================================
     CONTACT FORM STATE
  ========================================================= */

  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [contactLoading, setContactLoading] = useState(false);
  const [contactStatus, setContactStatus] = useState("");


  /* =========================================================
     REVIEW FORM STATE
  ========================================================= */

  const [reviewForm, setReviewForm] = useState({
    customerName: "",
    company: "",
    service: "",
    rating: 0,
    review: "",
  });

  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewStatus, setReviewStatus] = useState("");


  /* =========================================================
     HONEYPOT
  ========================================================= */

  const [website, setWebsite] = useState("");


  /* =========================================================
     CONTACT INPUT HANDLER
  ========================================================= */

  const handleContactChange = (e) => {

    const { name, value } = e.target;

    setContactForm((previous) => ({
      ...previous,
      [name]: value,
    }));

    setContactStatus("");
  };


  /* =========================================================
     REVIEW INPUT HANDLER
  ========================================================= */

  const handleReviewChange = (e) => {

    const { name, value } = e.target;

    setReviewForm((previous) => ({
      ...previous,
      [name]: value,
    }));

    setReviewStatus("");
  };


  /* =========================================================
     STAR RATING
  ========================================================= */

  const handleRating = (rating) => {

    setReviewForm((previous) => ({
      ...previous,
      rating: rating,
    }));

    setReviewStatus("");
  };


  /* =========================================================
     CONTACT VALIDATION
  ========================================================= */

  const validateContactForm = () => {

    if (!contactForm.name.trim()) {
      return "Please enter your name.";
    }

    if (!contactForm.email.trim()) {
      return "Please enter your email address.";
    }

    const emailPattern =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailPattern.test(contactForm.email.trim())) {
      return "Please enter a valid email address.";
    }

    if (!contactForm.message.trim()) {
      return "Please enter your project message.";
    }

    if (contactForm.message.trim().length < 10) {
      return "Please provide a little more information about your project.";
    }

    return null;
  };


  /* =========================================================
     REVIEW VALIDATION
  ========================================================= */

  const validateReviewForm = () => {

    if (!reviewForm.customerName.trim()) {
      return "Please enter your name.";
    }

    if (reviewForm.customerName.trim().length < 2) {
      return "Please enter a valid name.";
    }

    if (!reviewForm.rating || Number(reviewForm.rating) < 1) {
      return "Please select a star rating.";
    }

    if (!reviewForm.review.trim()) {
      return "Please write your review.";
    }

    if (reviewForm.review.trim().length < 15) {
      return "Please write at least 15 characters in your review.";
    }

    return null;
  };


  /* =========================================================
     CONTACT FORM SUBMIT
  ========================================================= */

  const handleContactSubmit = async (e) => {

    e.preventDefault();

    setContactStatus("");

    /*
      Spam protection.
      Hidden honeypot field should normally remain empty.
    */

    if (website.trim() !== "") {
      return;
    }

    const validationError =
      validateContactForm();

    if (validationError) {
      setContactStatus(validationError);
      return;
    }

    setContactLoading(true);

    try {

      await addDoc(
        collection(db, "contacts"),
        {
          name: contactForm.name.trim(),

          email:
            contactForm.email
              .trim()
              .toLowerCase(),

          phone:
            contactForm.phone.trim(),

          message:
            contactForm.message.trim(),

          type: "contact",

          status: "new",

          createdAt:
            serverTimestamp(),

          timestamp:
            serverTimestamp(),
        }
      );

      setContactStatus(
        "Message sent successfully. We will contact you soon."
      );

      setContactForm({
        name: "",
        email: "",
        phone: "",
        message: "",
      });

    } catch (error) {

      console.error(
        "CONTACT FORM ERROR:",
        error
      );

      setContactStatus(
        "Unable to send your message right now. Please contact us on WhatsApp."
      );

    } finally {

      setContactLoading(false);

    }
  };


  /* =========================================================
     REVIEW FORM SUBMIT
  ========================================================= */

  const handleReviewSubmit = async (e) => {

    e.preventDefault();

    setReviewStatus("");

    /*
      Spam protection
    */

    if (website.trim() !== "") {
      return;
    }

    const validationError =
      validateReviewForm();

    if (validationError) {
      setReviewStatus(validationError);
      return;
    }

    setReviewLoading(true);

    try {

      /*
        IMPORTANT

        Reviews are submitted as pending.

        They will NOT automatically become public.
      */

      const reviewData = {

        customerName:
          reviewForm.customerName.trim(),

        company:
          reviewForm.company.trim(),

        service:
          reviewForm.service.trim(),

        rating:
          Number(reviewForm.rating),

        review:
          reviewForm.review.trim(),

        approved: false,

        published: false,

        featured: false,

        status: "pending",

        createdAt:
          serverTimestamp(),

        timestamp:
          serverTimestamp(),
      };


      console.log(
        "Submitting review:",
        reviewData
      );


      /*
        Save into your existing
        Firestore collection:

        reviews
      */

      await addDoc(
        collection(db, "reviews"),
        reviewData
      );


      console.log(
        "Review submitted successfully."
      );


      setReviewStatus(
        "Thank you! Your review has been submitted and is waiting for approval."
      );


      setReviewForm({
        customerName: "",
        company: "",
        service: "",
        rating: 0,
        review: "",
      });


    } catch (error) {

      console.error(
        "REVIEW SUBMISSION ERROR:",
        error
      );


      /*
        Firebase permission error
      */

      if (
        error.code ===
        "permission-denied"
      ) {

        setReviewStatus(
          "Firebase blocked the review submission. Please check your Firestore rules for the reviews collection."
        );

      } else {

        setReviewStatus(
          `Unable to submit your review. ${
            error.message ||
            "Please try again."
          }`
        );

      }

    } finally {

      setReviewLoading(false);

    }
  };


  /* =========================================================
     STRUCTURED DATA
  ========================================================= */

  const structuredData = {

    "@context":
      "https://schema.org",

    "@graph": [

      {
        "@type": [
          "LocalBusiness",
          "ProfessionalService",
        ],

        "@id":
          `${SITE_URL}/#business`,

        name:
          "SamSoftix",

        url:
          SITE_URL,

        logo:
          `${SITE_URL}/logo.png`,

        image:
          `${SITE_URL}/og-image.jpg`,

        telephone:
          "+92-332-0472184",

        email:
          "info@samsoftix.com",

        address: {
          "@type":
            "PostalAddress",

          addressLocality:
            "Sambrial",

          addressRegion:
            "Punjab",

          addressCountry:
            "PK",
        },

        areaServed: [
          "Sambrial",
          "Sialkot",
          "Pakistan",
          "United Arab Emirates",
          "Saudi Arabia",
          "United Kingdom",
          "United States",
        ],

        sameAs: [
          "https://www.facebook.com/samsoftix",
          "https://www.linkedin.com/company/samsoftix",
          "https://www.instagram.com/samsoftix",
        ],
      },


      {
        "@type":
          "Organization",

        "@id":
          `${SITE_URL}/#organization`,

        name:
          "SamSoftix",

        url:
          SITE_URL,

        logo:
          `${SITE_URL}/logo.png`,

        email:
          "info@samsoftix.com",

        telephone:
          "+92-332-0472184",
      },


      {
        "@type":
          "WebPage",

        "@id":
          `${SITE_URL}/contact#webpage`,

        url:
          `${SITE_URL}/contact`,

        name:
          "Contact SamSoftix",

        description:
          "Contact SamSoftix for website development, mobile apps, ecommerce, AI solutions, custom software, SEO and digital services.",

        about: {
          "@id":
            `${SITE_URL}/#organization`,
        },
      },


      {
        "@type":
          "BreadcrumbList",

        itemListElement: [

          {
            "@type":
              "ListItem",

            position: 1,

            name:
              "Home",

            item:
              SITE_URL,
          },

          {
            "@type":
              "ListItem",

            position: 2,

            name:
              "Contact",

            item:
              `${SITE_URL}/contact`,
          },

        ],
      },

    ],
  };


  /* =========================================================
     PAGE
  ========================================================= */

  return (

    <div className="contact-page">


      {/* =====================================================
          SEO
      ===================================================== */}

      <Helmet>

        <title>
          Contact SamSoftix | Software House & IT Services
        </title>

        <meta
          name="description"
          content="Contact SamSoftix for professional website development, mobile apps, ecommerce, AI, custom software, SEO and digital solutions in Sambrial, Sialkot and Pakistan."
        />

        <meta
          name="keywords"
          content="contact SamSoftix, software house Sambrial, software house Sialkot, web development Sambrial, web development Sialkot, mobile app development Pakistan, ecommerce development Pakistan, AI development Pakistan, custom software Pakistan"
        />

        <meta
          name="robots"
          content="index, follow"
        />

        <link
          rel="canonical"
          href={`${SITE_URL}/contact`}
        />


        {/* Open Graph */}

        <meta
          property="og:title"
          content="Contact SamSoftix | Software House & IT Services"
        />

        <meta
          property="og:description"
          content="Contact SamSoftix for professional web, mobile, ecommerce, AI and custom software development."
        />

        <meta
          property="og:url"
          content={`${SITE_URL}/contact`}
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          property="og:image"
          content={`${SITE_URL}/og-image.jpg`}
        />


        {/* Twitter */}

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Contact SamSoftix"
        />

        <meta
          name="twitter:description"
          content="Contact SamSoftix for professional software and digital services."
        />

        <meta
          name="twitter:image"
          content={`${SITE_URL}/og-image.jpg`}
        />


        {/* Structured Data */}

        <script type="application/ld+json">
          {JSON.stringify(
            structuredData
          )}
        </script>

      </Helmet>


      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="contact-header">

        <div className="contact-header-content">

          <span className="contact-eyebrow">
            LET'S BUILD SOMETHING GREAT
          </span>

          <h1>

            <span className="contact-highlight">Contact{" "}</span>

            <span className="contact-highlight">
              SamSoftix
            </span>

          </h1>

          <p>
            Have a project, business idea,
            website requirement or software
            challenge? Talk to our team.
          </p>

        </div>

      </section>


      {/* =====================================================
          CONTACT SECTION
      ===================================================== */}

      <section className="contact-main">


        {/* ===================================================
            CONTACT DETAILS
        =================================================== */}

        <div className="contact-details">

          <div className="contact-section-title">

            <span>
              CONTACT INFORMATION
            </span>

            <h2>
              Let's talk about your project
            </h2>

            <p>
              Tell us what you need and our
              team will get back to you.
            </p>

          </div>


          {/* EMAIL */}

          <div className="detail-box">

            <div className="detail-icon">
              ✉
            </div>

            <div>

              <span>
                Email
              </span>

              <a
                href="mailto:info@samsoftix.com"
              >
                info@samsoftix.com
              </a>

            </div>

          </div>


          {/* PHONE */}

          <div className="detail-box">

            <div className="detail-icon">
              ☎
            </div>

            <div>

              <span>
                WhatsApp / Phone
              </span>

              <a
                href="tel:+923320472184"
              >
                +92 332 0472184
              </a>

            </div>

          </div>


          {/* LOCATION */}

          <div className="detail-box">

            <div className="detail-icon">
              📍
            </div>

            <div>

              <span>
                Office
              </span>

              <p>
                Sambrial, Punjab, Pakistan
              </p>

            </div>

          </div>


          {/* SOCIAL */}

          <div className="social-area">

            <h3>
              Connect with us
            </h3>

            <div className="social-links">


              <a
                href="https://www.facebook.com/samsoftix"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon-text"
              >

                <FaFacebookF />

                Facebook

              </a>


              <a
                href="https://www.linkedin.com/company/samsoftix"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon-text"
              >

                <FaLinkedinIn />

                LinkedIn

              </a>


              <a
                href="https://www.instagram.com/samsoftix"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon-text"
              >

                <FaInstagram />

                Instagram

              </a>


            </div>

          </div>

        </div>


        {/* ===================================================
            CONTACT FORM
        =================================================== */}

        <div className="contact-form-wrapper">

          <div className="contact-section-title">

            <span>
              PROJECT INQUIRY
            </span>

            <h2>
              Tell us about your project
            </h2>

          </div>


          <form
            className="contact-form"
            onSubmit={
              handleContactSubmit
            }
          >


            {/* HONEYPOT */}

            <input
              type="text"
              name="website"
              value={website}
              onChange={(e) =>
                setWebsite(
                  e.target.value
                )
              }
              tabIndex="-1"
              autoComplete="off"
              className="honeypot-field"
              aria-hidden="true"
            />


            <div className="form-grid">


              {/* NAME */}

              <div className="form-group">

                <label htmlFor="name">
                  Your Name *
                </label>

                <input
                  id="name"
                  type="text"
                  name="name"
                  value={
                    contactForm.name
                  }
                  onChange={
                    handleContactChange
                  }
                  placeholder="Enter your name"
                  autoComplete="name"
                  required
                />

              </div>


              {/* EMAIL */}

              <div className="form-group">

                <label htmlFor="email">
                  Email Address *
                </label>

                <input
                  id="email"
                  type="email"
                  name="email"
                  value={
                    contactForm.email
                  }
                  onChange={
                    handleContactChange
                  }
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                />

              </div>


            </div>


            {/* PHONE */}

            <div className="form-group">

              <label htmlFor="phone">
                Phone / WhatsApp
              </label>

              <input
                id="phone"
                type="tel"
                name="phone"
                value={
                  contactForm.phone
                }
                onChange={
                  handleContactChange
                }
                placeholder="+92 3XX XXXXXXX"
                autoComplete="tel"
              />

            </div>


            {/* MESSAGE */}

            <div className="form-group">

              <label htmlFor="message">
                Project Details *
              </label>

              <textarea
                id="message"
                name="message"
                value={
                  contactForm.message
                }
                onChange={
                  handleContactChange
                }
                placeholder="Tell us about your project..."
                rows="7"
                required
              />

            </div>


            {/* STATUS */}

            {contactStatus && (

              <div
                className={
                  contactStatus.includes(
                    "successfully"
                  )
                    ? "form-success"
                    : "form-status-error"
                }
              >
                {contactStatus}
              </div>

            )}


            {/* BUTTON */}

            <button
              type="submit"
              className="contact-submit-btn"
              disabled={contactLoading}
            >

              {contactLoading
                ? "Sending..."
                : "Send Project Inquiry"}

            </button>


          </form>

        </div>

      </section>


      {/* =====================================================
          REVIEW SECTION
      ===================================================== */}

      <section className="review-section">


        <div className="review-header">

          <span className="review-eyebrow">
            CLIENT FEEDBACK
          </span>
          <span className="myh2"><h2>
            Share Your Experience
          </h2></span>

          <p>
            Worked with SamSoftix? Tell us
            about your experience. Your feedback
            helps us improve our services.
          </p>

        </div>


        {/* REVIEW FORM */}

        <div className="review-form-wrapper">

          <form
            className="review-form"
            onSubmit={
              handleReviewSubmit
            }
          >


            {/* HONEYPOT */}

            <input
              type="text"
              name="reviewWebsite"
              value={website}
              onChange={(e) =>
                setWebsite(
                  e.target.value
                )
              }
              tabIndex="-1"
              autoComplete="off"
              className="honeypot-field"
              aria-hidden="true"
            />


            {/* NAME + COMPANY */}

            <div className="review-input-grid">


              <div className="form-group">

                <label htmlFor="customerName">
                  Your Name *
                </label>

                <input
                  id="customerName"
                  type="text"
                  name="customerName"
                  value={
                    reviewForm.customerName
                  }
                  onChange={
                    handleReviewChange
                  }
                  placeholder="Your name"
                  autoComplete="name"
                  required
                />

              </div>


              <div className="form-group">

                <label htmlFor="company">
                  Company
                </label>

                <input
                  id="company"
                  type="text"
                  name="company"
                  value={
                    reviewForm.company
                  }
                  onChange={
                    handleReviewChange
                  }
                  placeholder="Company name"
                />

              </div>


            </div>


            {/* SERVICE + RATING */}

            <div className="review-input-grid">


              {/* SERVICE */}

              <div className="form-group">

                <label htmlFor="service">
                  Service Used
                </label>

                <select
                  id="service"
                  name="service"
                  value={
                    reviewForm.service
                  }
                  onChange={
                    handleReviewChange
                  }
                >

                  <option value="">
                    Select a service
                  </option>

                  <option value="Website Development">
                    Website Development
                  </option>

                  <option value="Mobile App Development">
                    Mobile App Development
                  </option>

                  <option value="Ecommerce Development">
                    Ecommerce Development
                  </option>

                  <option value="AI & ML Solutions">
                    AI & ML Solutions
                  </option>

                  <option value="Custom Software">
                    Custom Software
                  </option>

                  <option value="SEO & Digital Marketing">
                    SEO & Digital Marketing
                  </option>

                  <option value="Training">
                    Training
                  </option>

                  <option value="Other">
                    Other
                  </option>

                </select>

              </div>


              {/* RATING */}

              <div className="form-group">

                <label>
                  Your Rating *
                </label>

                <div
                  className="star-rating"
                  role="radiogroup"
                  aria-label="Review rating"
                >

                  {[1, 2, 3, 4, 5].map(
                    (star) => (

                      <button
                        key={star}
                        type="button"
                        className={
                          star <=
                          reviewForm.rating
                            ? "star active"
                            : "star"
                        }
                        onClick={() =>
                          handleRating(star)
                        }
                        aria-label={`${star} star`}
                        aria-checked={
                          star ===
                          reviewForm.rating
                        }
                        role="radio"
                      >

                        <FaStar />

                      </button>

                    )
                  )}

                </div>

              </div>

            </div>


            {/* REVIEW */}

            <div className="form-group">

              <label htmlFor="review">
                Your Review *
              </label>

              <textarea
                id="review"
                name="review"
                value={
                  reviewForm.review
                }
                onChange={
                  handleReviewChange
                }
                placeholder="Tell us about your experience with SamSoftix..."
                rows="6"
                minLength="15"
                maxLength="1000"
                required
              />

              <small>
                {reviewForm.review.length}
                /1000 characters
              </small>

            </div>


            {/* REVIEW STATUS */}

            {reviewStatus && (

              <div
                className={
                  reviewStatus.includes(
                    "Thank you"
                  )
                    ? "form-success"
                    : "form-status-error"
                }
              >

                {reviewStatus}

              </div>

            )}


            {/* SUBMIT REVIEW */}

            <button
              type="submit"
              className="review-submit-btn"
              disabled={reviewLoading}
            >

              {reviewLoading
                ? "Submitting Review..."
                : "Submit Review"}

            </button>


            <p className="review-note">

              Your review will be checked by
              our team before being published.

            </p>


          </form>

        </div>

      </section>


      {/* =====================================================
          WHATSAPP CTA
      ===================================================== */}

      <section className="whatsapp-cta">

        <div>

          <span>
            NEED A QUICK RESPONSE?
          </span>

          <h2>
            Talk to SamSoftix on WhatsApp
          </h2>

          <p>
            Send us your project requirements
            directly and let's discuss your idea.
          </p>

        </div>


        <a
          href="https://wa.me/923320472184"
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-button"
        >

          <FaWhatsapp />

          Chat on WhatsApp

        </a>

      </section>


    </div>

  );
}


export default Contact;